'use client';

import { useMemo } from 'react';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { ProductRail } from '@eliadmin/core/src/components/dlz/home/products/product-rail';
import { Heading } from '@eliadmin/core/src/components/dlz/ui/heading';
import Link from '@eliadmin/core/src/components/link';
import { RelatedProductsStatus } from '@eliadmin/core/src/components/product/detail/related-products-status';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { useRelatedProducts } from '@eliadmin/core/src/hooks/products/use-related-products';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { getFlatBreadcrumbs } from '@eliadmin/core/src/utils/url-utils';

/** Recommended accessories first, then the related products; a product is listed once. */
function mergeProducts(accessories: Product[], related: Product[]) {
  const seen = new Set<number>();
  return [...accessories, ...related].filter(product => {
    if (product?.id == null || seen.has(product.id)) return false;
    seen.add(product.id);
    return true;
  });
}

/**
 * Figma „Section · Sale“ 60137:7645 – „Súvisiace produkty“: title with the two round arrow buttons
 * and a row of horizontal product tiles (two per view at 1328 px). Data and states follow the core
 * `ProductRecomendedProducts`: related products of the detail slug plus the recommended accessories
 * (when the template enables them), a non-blocking loading / error status, nothing when empty.
 */
export function RelatedProducts() {
  const { t } = useTranslations();
  const product = useVariantsStore(s => s.product);
  const currency = useGlobalStore(s => s.generalLayout?.currency);
  const accessoriesEnabled = useGlobalStore(
    s => !!s.templateSettings.product_detail?.product_recommended_accessories?.enabled,
  );
  const related = useRelatedProducts(product.slug);

  const products = useMemo(
    () => mergeProducts(accessoriesEnabled ? (product.accessories ?? []) : [], related.products),
    [accessoriesEnabled, product.accessories, related.products],
  );
  const failed = related.enabled && related.isError;

  if (!products.length && !related.isLoading && !failed) return null;

  const title = t('text744');
  // „Všetky produkty“ (mobile only) leads to the deepest category of the product.
  const category = product.breadcrumb ? getFlatBreadcrumbs(product.breadcrumb).at(-1) : undefined;

  return (
    <section
      data-section="product-related"
      aria-labelledby="product-related-title"
      className="overflow-x-clip py-6 lg:py-10"
    >
      <Container>
        {products.length ? (
          <>
            <ProductListSchema products={products} currency={currency} />
            <ProductRail
              products={products}
              label={title}
              layout="compact"
              progress
              title={<Heading id="product-related-title" title={title} size="h2" />}
              footerAction={
                category?.slug ? (
                  <Button asChild variant="secondary">
                    <Link href={category.slug}>{t('text494')}</Link>
                  </Button>
                ) : null
              }
            />
          </>
        ) : (
          <Heading id="product-related-title" title={title} size="h2" />
        )}
        <RelatedProductsStatus
          loading={related.isLoading}
          error={failed}
          retry={() => {
            void related.refetch();
          }}
        />
      </Container>
    </section>
  );
}
